import { useState } from 'react';

import { Accordion, Box, Card } from '@zillow/constellation-10';

import {
  type AccountViewListingGroupDto,
  AccountViewListingGroupType,
  type RentalManagerAddressDto,
} from '@zg-rentals/ts-rental-manager-api-client';

import { useFlexiblePropertiesSearch } from '../../../contexts/FlexiblePropertiesSearchContext';
import { getFormattedAddress, isMultiplexListing } from '../../../utils/flexiblePropertyCardUtils';

import {
  accordionHeaderWithTrailingControlStyles,
  cardHeaderRowStyles,
  cardPanelStyles,
} from '../../../components/AccordionCardList/styles';
import EditMultiplexPropertyModal from '../../../components/modals/EditMultiplexPropertyModal';
import FlexibleArchivePropertyModal, {
  ARCHIVE_TARGET_NOUN,
  type ArchiveTargetNoun,
} from '../../../components/modals/FlexibleArchivePropertyModal';
import PropertyDetailsModal from '../../../components/modals/PropertyDetailsModal';

import { cardStyles, unitsTableContainerStyles } from '../../../styles/propertyCardTable';
import { overflowMenuStyles } from '../styles';

import FlexiblePropertyCardHeader from './FlexiblePropertyCardHeader';
import PropertyOverflowMenu from './PropertyOverflowMenu';
import UnitsTable from './UnitsTable';

type Props = {
  listing: AccountViewListingGroupDto;
  isExpanded: boolean;
  isLoading?: boolean;
};

type ArchiveTarget = {
  listingAlias: string;
  groupId: string;
  address: RentalManagerAddressDto;
  targetNoun: ArchiveTargetNoun;
  listingType: AccountViewListingGroupType;
  isArchived?: boolean;
  unitNumber?: string;
  unitCount?: number;
};

const FlexiblePropertyCard = ({ listing, isExpanded, isLoading = false }: Props) => {
  const { includeArchived: isArchiveView } = useFlexiblePropertiesSearch();

  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [showMultiplexEditModal, setShowMultiplexEditModal] = useState(false);
  const [showUnitsEditModal, setShowUnitsEditModal] = useState(false);
  const [editListingAlias, setEditListingAlias] = useState('');
  const isMultiplex = isMultiplexListing(listing);
  const formattedAddress = getFormattedAddress(listing);

  return (
    <>
      <Card.Root data-testid="FlexiblePropertyCard" tone="neutral" outlined={true} elevated={false} css={cardStyles}>
        <Box css={cardHeaderRowStyles}>
          <Accordion.Header
            data-testid="FlexiblePropertyCard-ExpandTrigger"
            css={accordionHeaderWithTrailingControlStyles}
          >
            <FlexiblePropertyCardHeader listing={listing} isLoading={isLoading} />
          </Accordion.Header>

          {/* Sibling of the trigger, not a child of it, so the two interactive controls never nest. */}
          <Box css={overflowMenuStyles}>
            <PropertyOverflowMenu
              groupId={listing.groupId}
              listingAlias={listing.listingAlias}
              addressLabel={formattedAddress}
              address={listing.address}
              onArchiveClick={(listingAlias, address) =>
                setArchiveTarget({
                  listingAlias,
                  groupId: listing.groupId,
                  address,
                  targetNoun: ARCHIVE_TARGET_NOUN.PROPERTY,
                  listingType: listing.type,
                  isArchived: isArchiveView,
                  unitCount: listing.listingCount?.total,
                })
              }
              onEditClick={
                isMultiplex
                  ? () => {
                      setShowMultiplexEditModal(true);
                    }
                  : () => {
                      setEditListingAlias(listing.listingAlias);
                      setShowUnitsEditModal(true);
                    }
              }
            />
          </Box>
        </Box>

        <Accordion.Panel css={cardPanelStyles}>
          {/* Accordion.Panel keeps children mounted while collapsed; without this guard every
              collapsed card would fetch its units, statuses, and tenants. */}
          {isExpanded && (
            <Box data-testid="UnitsTableContainer" css={unitsTableContainerStyles}>
              <UnitsTable
                listing={listing}
                onArchiveClick={(listingAlias, address, unitNumber) =>
                  setArchiveTarget({
                    listingAlias,
                    groupId: listing.groupId,
                    address,
                    targetNoun: ARCHIVE_TARGET_NOUN.UNIT,
                    listingType: AccountViewListingGroupType.LISTING,
                    isArchived: isArchiveView,
                    unitNumber,
                  })
                }
                onEditClick={(listingAlias) => {
                  setEditListingAlias(listingAlias);
                  setShowUnitsEditModal(true);
                }}
              />
            </Box>
          )}
        </Accordion.Panel>
      </Card.Root>

      {archiveTarget && (
        <FlexibleArchivePropertyModal
          onClose={() => setArchiveTarget(null)}
          isArchived={archiveTarget.isArchived ?? false}
          listingAlias={archiveTarget.listingAlias}
          groupId={archiveTarget.groupId}
          address={archiveTarget.address}
          targetNoun={archiveTarget.targetNoun}
          listingType={archiveTarget.listingType}
          unitNumber={archiveTarget.unitNumber}
          unitCount={archiveTarget.unitCount}
        />
      )}

      {showMultiplexEditModal && (
        <EditMultiplexPropertyModal
          isOpen={true}
          groupId={listing.groupId}
          fullAddress={formattedAddress}
          address={listing.address}
          unitCount={listing.listingCount?.total}
          onClose={() => setShowMultiplexEditModal(false)}
          onSuccess={() => setShowMultiplexEditModal(false)}
        />
      )}

      {showUnitsEditModal && (
        <PropertyDetailsModal
          isOpen={true}
          listingAlias={editListingAlias}
          submitLabel="Save changes"
          onClose={() => setShowUnitsEditModal(false)}
          onConfirm={() => setShowUnitsEditModal(false)}
        />
      )}
    </>
  );
};

export default FlexiblePropertyCard;
